import type { Holiday } from "../types/holiday.types";
import { HolidayKind } from "../constants/holiday.constants";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function str(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

function bool(v: unknown): boolean {
  if (v === true || v === "true" || v === 1 || v === "1") return true;
  return false;
}

function kindFromRaw(v: unknown): HolidayKind {
  const s = str(v).toUpperCase();
  if (s === HolidayKind.COMPANY) return HolidayKind.COMPANY;
  if (s === HolidayKind.OPTIONAL) return HolidayKind.OPTIONAL;
  return HolidayKind.PUBLIC;
}

export function normalizeHoliday(raw: unknown): Holiday | null {
  if (!isRecord(raw)) return null;
  const r = raw;
  const id = Number(r.id ?? 0);
  if (!Number.isFinite(id) || id <= 0) return null;

  const catsRaw = r.employmentCategories ?? r.employment_categories;
  const idsRaw = r.employmentCategoryIds ?? r.employment_category_ids;
  let employmentCategories: Holiday["employmentCategories"] = null;
  if (Array.isArray(catsRaw)) {
    employmentCategories = catsRaw
      .map((c) => {
        if (!isRecord(c)) return null;
        const cid = Number(c.id ?? 0);
        const name = str(c.name);
        if (!Number.isFinite(cid) || cid <= 0 || !name) return null;
        return { id: cid, name };
      })
      .filter(Boolean) as Holiday["employmentCategories"];
  }

  let employmentCategoryIds: number[] | undefined;
  if (Array.isArray(idsRaw)) {
    employmentCategoryIds = idsRaw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  } else if (employmentCategories?.length) {
    employmentCategoryIds = employmentCategories.map((c) => c.id);
  }

  const dateStr = str(r.date);
  const date =
    dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;

  return {
    id,
    name: str(r.name),
    date,
    kind: kindFromRaw(r.kind),
    isPaid: bool(r.isPaid ?? r.is_paid),
    applicableToAll: bool(r.applicableToAll ?? r.applicable_to_all),
    employmentCategoryIds,
    employmentCategories,
    createdAt: str(r.createdAt ?? r.created_at) || undefined,
    updatedAt: str(r.updatedAt ?? r.updated_at) || undefined,
  };
}
